/**
 * ModalVerEquipo.js — Modal unificado para ver equipo de cualquier jugador.
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';

const ORDEN_POSICIONES = ['ARQ', 'DEF', 'VOL', 'DEL'];

const COLOR_POS = {
  ARQ: { bg: 'rgba(251,191,36,0.15)', border: '#f59e0b', text: '#f59e0b' },
  DEF: { bg: 'rgba(34,197,94,0.12)',  border: '#22c55e', text: '#22c55e' },
  VOL: { bg: 'rgba(59,130,246,0.12)', border: '#3b82f6', text: '#3b82f6' },
  DEL: { bg: 'rgba(239,68,68,0.12)',  border: '#ef4444', text: '#ef4444' },
};

const BadgePos = ({ pos }) => {
  const c = COLOR_POS[pos] || { bg: 'rgba(100,116,139,0.15)', border: '#64748b', text: '#64748b' };
  return (
    <span style={{
      background: c.bg, border: `1px solid ${c.border}`, color: c.text,
      borderRadius: '4px', padding: '1px 6px', fontSize: '0.65rem',
      fontWeight: 'bold', letterSpacing: '0.5px', flexShrink: 0,
    }}>
      {pos}
    </span>
  );
};

const FilaJugador = ({ jugador, isSuplente, esCapitan, puntos }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '7px 10px',
    background: esCapitan ? 'rgba(245,158,11,0.07)' : isSuplente ? '#060b12' : '#0a1020',
    borderRadius: '6px',
    borderLeft: `2px solid ${esCapitan ? '#f59e0b' : isSuplente ? '#1e293b' : '#1e3a5f'}`,
    opacity: isSuplente ? 0.8 : 1,
    transition: 'background 0.15s',
  }}>
    <BadgePos pos={jugador.posicion?.sigla || jugador._posLabel || '??'} />
    <span style={{ flex: 1, color: esCapitan ? '#fde68a' : '#e2e8f0', fontSize: '0.85rem', fontWeight: esCapitan ? '600' : '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {jugador.apellido}
      {jugador.nombre && <span style={{ color: '#64748b', fontWeight: '400' }}>, {jugador.nombre.split(' ')[0]}</span>}
    </span>
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
      {jugador.club?.sigla && (
        <span style={{ background: '#1e293b', color: '#94a3b8', borderRadius: '3px', padding: '1px 5px', fontSize: '0.65rem', fontWeight: 'bold' }}>
          {jugador.club.sigla}
        </span>
      )}
      {jugador.nacionalidad && (
        <span style={{ color: '#475569', fontSize: '0.65rem' }}>
          {jugador.nacionalidad.slice(0, 3).toUpperCase()}
        </span>
      )}
      {jugador.pie_habil && (
        <span style={{ color: '#334155', fontSize: '0.6rem' }}>
          {jugador.pie_habil === 'Izquierdo' ? 'IZQ' : 'DER'}
        </span>
      )}
      <span style={{ color: '#00c8d7', fontSize: '0.75rem', fontWeight: 'bold', minWidth: '40px', textAlign: 'right' }}>
        ${(jugador.precio / 1_000_000).toFixed(1)}M
      </span>
      <span style={{
        background: esCapitan && puntos != null && puntos > 0 ? 'rgba(245,158,11,0.15)' : puntos != null && puntos > 0 ? 'rgba(34,197,94,0.12)' : 'transparent',
        border: `1px solid ${esCapitan && puntos != null ? '#f59e0b66' : puntos != null && puntos > 0 ? '#22c55e44' : 'transparent'}`,
        color: esCapitan && puntos != null && puntos > 0 ? '#fde68a' : puntos != null && puntos > 0 ? '#86efac' : '#334155',
        borderRadius: '4px', padding: '1px 6px',
        fontSize: '0.82rem', fontWeight: 'bold',
        minWidth: '34px', textAlign: 'center',
      }}>
        {puntos != null && puntos > 0 ? `+${puntos}` : '—'}
      </span>
      <span style={{
        background: esCapitan ? '#f59e0b' : 'transparent',
        color: esCapitan ? '#000' : 'transparent',
        borderRadius: '50%', width: '16px', height: '16px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.6rem', fontWeight: '900', flexShrink: 0,
      }}>C</span>
    </div>
  </div>
);

const SeccionJugadores = ({ titulo, jugadores, tipoColor, capitan_index, puntos_por_jugador }) => {
  if (!jugadores.length) return null;
  const porPos = {};
  ORDEN_POSICIONES.forEach(p => { porPos[p] = []; });
  jugadores.forEach(j => {
    const pos = j.posicion?.sigla || j._posLabel || 'DEL';
    if (!porPos[pos]) porPos[pos] = [];
    porPos[pos].push(j);
  });
  const grupos = ORDEN_POSICIONES.filter(p => porPos[p].length > 0);
  return (
    <div style={{ marginBottom: '16px' }}>
      <p style={{ color: '#475569', fontSize: '0.65rem', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: 'bold' }}>
        {titulo}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        {titulo === 'Suplentes'
          ? jugadores.map((j, i) => (
              <FilaJugador key={i} jugador={j} isSuplente esCapitan={false} puntos={puntos_por_jugador?.[j.id] ?? null} />
            ))
          : grupos.map(pos =>
              porPos[pos].map((j, i) => (
                <FilaJugador
                  key={`${pos}-${i}`}
                  jugador={j}
                  isSuplente={false}
                  esCapitan={j._slot === capitan_index}
                  puntos={puntos_por_jugador?.[j.id] ?? null}
                />
              ))
            )
        }
      </div>
    </div>
  );
};

// ── Cuántos jugadores mostrar por restricción (null = no mostrar ninguno) ──
const LIMITE_RELEVANTES = {
  'al menos 3 zurdos': 3,
  'minimo 1 sub-23': 1,
  'minimo 2 jugadores con +33 anos': 2,
  'maximo 2 jugadores con +33 anos': null,
  'maximo 4 jugadores de mas de $65m': null,
  'maximo 2 jugadores de la ultima final del mundial': null,
  'minimo 2 ex serie a': 2,
  'minimo 2 ex premier league': 2,
  'minimo 2 ex la liga': 2,
  'minimo 2 ex ligue 1': 2,
  'minimo 2 ex bundesliga': 2,
  'minimo 1 ex eredivisie': 1,
  'minimo 2 serie a actual': 2,
  'minimo 2 la liga actual': 2,
  'minimo 2 ligue 1 actual': 2,
  'minimo 2 bundesliga actual': 2,
  'minimo 1 eredivisie actual': 1,
  'minimo 3 jugadores premier actual': 3,
  'minimo 2 defensores sudamericanos': 2,
  'minimo 2 defensores europeos': 2,
  'minimo 1 defensor asiatico': 1,
  'minimo 1 defensor africano': 1,
  'minimo 1 defensor de oceania': 1,
  'maximo 1 defensor europeo': null,
  'maximo 1 defensor sudamericano': null,
  'minimo 1 delantero asiatico': 1,
  'minimo 2 delanteros africanos': 2,
  'maximo 6 jugadores de selecciones campeonas del mundo': null,
  'maximo 4 europeos': null,
  'minimo 2 de america': 2,
  'minimo 3 de la misma seleccion': 3,
  'minimo 1 arquero zurdo': 1,
  'maximo 3 dorsales 10': null,
  'maximo 3 dorsales 9': null,
  'maximo 3 dorsales 7': null,
  'maximo 3 dorsales 11': null,
  'minimo 1 jugador de las 3 peores selecciones del ranking fifa': 1,
  'ningun jugador de las 3 mejores selecciones del ranking fifa': null,
  'minimo 3 jugadores con altura +182': 3,
  'minimo 3 jugadores con altura +180': 3,
};

// Normaliza el texto para comparar con las keys del mapa
const normKey = (s) =>
  (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[$\.+,]/g, '')
    .trim();

const ChipRestricciones = ({ restriccion, equipo, getJugadoresRelevantes }) => {
  const key = normKey(restriccion.texto);
  const limite = LIMITE_RELEVANTES[key];
  // limite === null  → no mostrar jugadores
  // limite === número → mostrar hasta ese número
  // limite === undefined → restricción no mapeada, mostrar todos (fallback)
  const mostrarJugadores = limite !== null;

  const todosRelevantes = (mostrarJugadores && getJugadoresRelevantes)
    ? getJugadoresRelevantes(equipo, restriccion.texto)
    : [];

  const relevantes = (typeof limite === 'number')
    ? todosRelevantes.slice(0, limite)
    : todosRelevantes;

  const cumplida = restriccion.cumplida;

  return (
    <div style={{
      background: '#080e1a',
      border: `1px solid ${cumplida ? '#22c55e33' : '#1e293b'}`,
      borderLeft: `3px solid ${cumplida ? '#22c55e' : '#334155'}`,
      borderRadius: '6px',
      padding: '9px 12px',
      transition: 'border-color 0.2s',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: relevantes.length ? '7px' : 0 }}>
        <span style={{ fontSize: '0.8rem', flexShrink: 0 }}>
          {cumplida ? '✓' : '·'}
        </span>
        <span style={{ color: cumplida ? '#86efac' : '#94a3b8', fontSize: '0.82rem', fontWeight: '500' }}>
          {restriccion.texto}
        </span>
        {restriccion.emoji && <span style={{ fontSize: '0.8rem', marginLeft: 'auto' }}>{restriccion.emoji}</span>}
      </div>
      {relevantes.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', paddingLeft: '20px' }}>
          {relevantes.map((j, i) => (
            <span key={i} style={{
              background: '#2a374b', color: '#dee5eb', borderRadius: '3px',
              padding: '1px 7px', fontSize: '0.7rem',
            }}>
              {j.apellido} <span style={{ color: '#628dc9' }}>{j.club?.sigla}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const ModalVerEquipo = ({ datos, onClose, tipoColor = '#00f2ff', getJugadoresRelevantes }) => {
  const [puntosBase, setPuntosBase] = useState({});
  useEffect(() => {
    if (!datos?.jornada_numero || !datos?.puntos_por_jugador) return;
    const ids = Object.keys(datos.puntos_por_jugador);
    if (!ids.length) return;
    supabase.from('jugador_puntos_fecha')
      .select('jugador_id, puntos_base')
      .eq('jornada', datos.jornada_numero)
      .in('jugador_id', ids)
      .then(({ data }) => {
        if (!data) return;
        const mapa = {};
        data.forEach(d => { mapa[d.jugador_id] = d.puntos_base; });
        setPuntosBase(mapa);
      });
  }, [datos?.jornada_numero, datos?.puntos_por_jugador]);

  if (!datos) return null;

  const { nombre, equipo = {}, formacion, presupuesto, restricciones = [], puntos, soloRestricciones, capitan_index, puntos_por_jugador, jornada_numero } = datos;

  const puntos_base_por_jugador = puntosBase;

  const titulares = Object.entries(equipo)
    .filter(([k, v]) => Number(k) <= 10 && v)
    .map(([k, j]) => ({ ...j, _slot: Number(k) }));

  const suplentes = Object.entries(equipo)
    .filter(([k, v]) => Number(k) >= 20 && v)
    .map(([k, j]) => ({ ...j, _slot: Number(k) }));

  // Calcular puntaje ajustado: capitán con base×2+bonus, suplentes filtrados
  const POS_SUPLENTE_M = { 20: 'ARQ', 21: 'DEF', 22: 'VOL', 23: 'DEL' };
  const SLOTS_TIT_M = [0,1,2,3,4,5,6,7,8,9,10];
  const puntosAjustados = (() => {
    if (!puntos_por_jugador || Object.keys(puntos_por_jugador).length === 0) return {};
    const resultado = { ...puntos_por_jugador };
    // Filtrar suplentes que no deben sumar
    [20, 21, 22, 23].forEach(slotSup => {
      const jug = equipo[String(slotSup)];
      if (!jug?.id) return;
      const posSup = POS_SUPLENTE_M[slotSup];
      const titsPos = SLOTS_TIT_M.filter(s => {
        const t = equipo[String(s)];
        const pos = t?.posicion?.sigla || t?.posicion;
        return t?.id && pos === posSup;
      });
      const titsConPts = titsPos.filter(s => {
        const t = equipo[String(s)];
        return t?.id && puntos_por_jugador[t.id] != null;
      });
      if (titsConPts.length >= titsPos.length) delete resultado[jug.id];
    });
    // No duplicar capitán — puntos_por_jugador ya viene con el valor correcto post-distribución
    return resultado;
  })();

  const restriccionesFiltradas = restricciones.filter(r => !r.anulada);

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#0b1120',
          border: `1px solid ${tipoColor}33`,
          borderTop: `2px solid ${tipoColor}`,
          borderRadius: '14px',
          padding: '0',
          width: '420px',
          maxWidth: '95vw',
          maxHeight: '86vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: `0 0 60px rgba(0,0,0,0.7), 0 0 20px ${tipoColor}11`,
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* HEADER */}
        <div style={{
          padding: '16px 20px 14px',
          borderBottom: '1px solid #1e293b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div>
            <h3 style={{ margin: 0, color: '#f1f5f9', fontSize: '1rem', fontWeight: '600' }}>
              {nombre}
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '3px', flexWrap: 'wrap' }}>
              {formacion && (
                <span style={{ color: tipoColor, fontSize: '0.72rem', fontFamily: 'monospace', background: `${tipoColor}15`, border: `1px solid ${tipoColor}33`, borderRadius: '3px', padding: '1px 6px' }}>
                  {formacion}
                </span>
              )}
              {presupuesto != null && (
                <span style={{ color: '#cfd3d9', fontSize: '0.72rem', background: '#1e293b', border: '1px solid #b3b7bc', borderRadius: '3px', padding: '1px 6px' }}>
                   {typeof presupuesto === 'string' ? presupuesto : `$${(presupuesto / 1_000_000).toFixed(0)}M`}
                </span>
              )}
              {soloRestricciones
                ? <span style={{ color: '#f59e0b', fontSize: '0.7rem' }}>Fecha en curso</span>
                : puntos != null && (
                    <span style={{ color: '#22c55e', fontSize: '0.72rem', fontWeight: 'bold', background: 'rgba(34,197,94,0.1)', border: '1px solid #22c55e33', borderRadius: '3px', padding: '1px 7px' }}>
                      {puntos} pts
                    </span>
                  )
              }
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: '1px solid #1e293b', borderRadius: '6px', color: '#64748b', cursor: 'pointer', fontSize: '1rem', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'border-color 0.2s', flexShrink: 0 }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#464f5d'; e.currentTarget.style.color = '#818b9a'; }}
          >
            ×
          </button>
        </div>

        {/* BODY */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {restriccionesFiltradas.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <p style={{ color: '#9aa0a8', fontSize: '0.65rem', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: 'bold' }}>
                Restricciones
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                {restriccionesFiltradas.map((r, i) => (
                  <ChipRestricciones
                    key={i}
                    restriccion={r}
                    equipo={equipo}
                    getJugadoresRelevantes={getJugadoresRelevantes}
                  />
                ))}
              </div>
            </div>
          )}
          {!soloRestricciones && (
            <>
              <SeccionJugadores titulo="Titulares" jugadores={titulares} tipoColor={tipoColor} capitan_index={capitan_index} puntos_por_jugador={puntosAjustados} />
              {suplentes.length > 0 && (
                <SeccionJugadores titulo="Suplentes" jugadores={suplentes} tipoColor={tipoColor} capitan_index={capitan_index} puntos_por_jugador={puntosAjustados} />
              )}
              {titulares.length === 0 && (
                <p style={{ color: '#334155', fontSize: '0.82rem', textAlign: 'center', margin: '20px 0' }}>
                  Sin jugadores registrados
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalVerEquipo;